import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useFlowium } from '../client/FlowiumContext';
import { ChevronRightIcon, SearchIcon, CloseIcon, NotificationIcon } from '../components/Icons';
import Avatar from '../components/Avatar';
import UpgradeButton from '../components/UpgradeButton';
import ShareModal from '../components/ShareModal';
import WorkspaceMembersModal from '../components/WorkspaceMembersModal';
import WorkspaceIcon from '../components/WorkspaceIcon';
import UserMenu from '../components/UserMenu';
import NotificationPanel from '../components/NotificationPanel';
import type { WorkspaceMember } from '@flowium/client';
import Input from '../components/Input';
import Button from '../components/Button';
import MobileScopeMenu from '../components/MobileScopeMenu';

interface NavbarProps {
    project: string;
    workspace: string;
    currentPath?: string;
}

const Navbar: React.FC<NavbarProps> = ({ project, workspace, currentPath = '' }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { currentWorkspace, currentProject, client, fetchUserData, logout, user } = useFlowium();
    const [isShareModalOpen, setIsShareModalOpen] = useState(false);
    const [isMembersModalOpen, setIsMembersModalOpen] = useState(false);
    const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
    const [isNotificationPanelOpen, setIsNotificationPanelOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [isSearchFocused, setIsSearchFocused] = useState(false);
    const [workspaceMembers, setWorkspaceMembers] = useState<WorkspaceMember[]>([]);
    const bellRef = useRef<HTMLDivElement>(null);
    const userMenuRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        let cancelled = false;
        const fetchMembers = async () => {
            if (currentWorkspace) {
                try {
                    const members = await client.getWorkspaceMembers(currentWorkspace.id);
                    if (cancelled) return;
                    setWorkspaceMembers(members);
                } catch (err) {
                    // silently handled
                }
            } else {
                setWorkspaceMembers([]);
            }
        };
        fetchMembers();
        return () => {
            cancelled = true;
        };
    }, [client, currentWorkspace]);

    const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchQuery(e.target.value);
    };

    const updateBrowserSearchQuery = React.useCallback((nextSearchQuery: string, replace = false) => {
        const params = new URLSearchParams(location.search);
        if (nextSearchQuery) {
            params.set('q', nextSearchQuery);
        } else {
            params.delete('q');
        }

        const queryString = params.toString();
        navigate(queryString ? `/browser?${queryString}` : '/browser', { replace });
    }, [location.search, navigate]);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const nextQuery = params.get('q') || '';
        setSearchQuery(prev => {
            if (isSearchFocused && prev !== nextQuery) {
                return prev;
            }
            return prev === nextQuery ? prev : nextQuery;
        });
    }, [location.search, isSearchFocused]);

    useEffect(() => {
        if (location.pathname !== '/browser') {
            return;
        }

        const params = new URLSearchParams(location.search);
        const currentQuery = params.get('q') || '';
        const nextQuery = searchQuery.trim();
        if (nextQuery === currentQuery) {
            return;
        }

        const timer = window.setTimeout(() => {
            updateBrowserSearchQuery(nextQuery, true);
        }, 300);

        return () => window.clearTimeout(timer);
    }, [searchQuery, location.pathname, location.search, updateBrowserSearchQuery]);

    const applySearchQuery = (nextSearchQuery: string) => {
        updateBrowserSearchQuery(nextSearchQuery);
    };

    const handleSearchSubmit = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            applySearchQuery(searchQuery.trim());
        }
    };

    const handleSearchBlur = () => {
        setIsSearchFocused(false);
        applySearchQuery(searchQuery.trim());
    };

    const clearSearch = () => {
        setSearchQuery('');
        applySearchQuery('');
    };

    const handleUserMenuClick = () => {
        setIsUserMenuOpen(true);
    };

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    const handleWorkspaceClick = async () => {
        // reset path and deselect project
        if (currentProject && currentWorkspace) {
            try {
                await client.setProject('', currentWorkspace.id);
                await fetchUserData();
            } catch (err) {
                // silently handled
            }
        }
        // navigate after project deselection completes
        navigate('/browser?path=');
    };

    const handleProjectClick = async () => {
        // reset path but keep project selected
        navigate('/browser');
    };

    // get first 3 members for avatars
    const avatarMembers = workspaceMembers.slice(0, 3).map((member, index) => ({
        bgColor: ['#52525B', '#71717A', '#3F3F46'][index],
        label: member.email.charAt(0).toUpperCase(),
        src: ''
    }));

    return (
        <div className="flex flex-wrap items-center gap-3 border-b border-secondary px-4 py-3 md:gap-4 md:px-6 md:py-4">
            <div className="flex min-w-0 flex-1 items-center gap-3">
                <MobileScopeMenu />
                {currentWorkspace && (
                    <div className="hidden lg:block">
                        <WorkspaceIcon
                            workspace={currentWorkspace}
                            isActive={false}
                            onClick={handleWorkspaceClick}
                            className="scale-[0.86]"
                        />
                    </div>
                )}

                <nav className="flex min-w-0 flex-wrap items-center gap-2 text-sm text-content-secondary">
                    <button
                        type="button"
                        className="truncate text-content-tertiary transition-colors hover:text-content"
                        onClick={handleWorkspaceClick}
                    >
                        {workspace}
                    </button>
                    {project && (
                        <>
                            <span className="flex items-center text-content-tertiary">
                                <ChevronRightIcon padding={0} size={3} />
                            </span>
                            <button
                                type="button"
                                className="truncate text-content-tertiary transition-colors hover:text-content"
                                onClick={handleProjectClick}
                            >
                                {project}
                            </button>
                        </>
                    )}
                    {currentPath && (
                        <>
                            <span className="flex items-center text-content-tertiary">
                                <ChevronRightIcon padding={0} size={3} />
                            </span>
                            <span className="truncate text-content">/{currentPath}</span>
                        </>
                    )}
                </nav>
            </div>

            <div className="order-3 w-full lg:order-none lg:max-w-md lg:flex-1">
                <Input
                    value={searchQuery}
                    onChange={handleSearch}
                    onKeyDown={handleSearchSubmit}
                    onFocus={() => setIsSearchFocused(true)}
                    onBlur={handleSearchBlur}
                    placeholder="Search files and folders"
                    size="sm"
                    leftIcon={<SearchIcon padding={0} size={4} />}
                    inputClassName="bg-surface-secondary"
                    rightAdornment={searchQuery ? (
                        <button
                            type="button"
                            onClick={clearSearch}
                            aria-label="Clear search"
                            className="rounded-full p-1 text-content-tertiary transition-colors hover:bg-hover hover:text-content"
                        >
                            <CloseIcon padding={0} size={3} />
                        </button>
                    ) : undefined}
                />
            </div>

            <div className="ml-auto flex items-center gap-1 sm:gap-2">
                <button
                    type="button"
                    onClick={() => setIsMembersModalOpen(true)}
                    aria-label="View workspace members (mobile)"
                    className="flex items-center rounded-full px-2 py-1 transition-colors hover:bg-hover lg:hidden"
                >
                    <div className="flex -space-x-2">
                        {avatarMembers.map((avatar, index) => (
                            <Avatar
                                key={index}
                                label={avatar.label}
                                src={avatar.src}
                                color={avatar.bgColor}
                                size={7}
                            />
                        ))}
                    </div>
                </button>

                <div className="relative lg:hidden" ref={bellRef}>
                    <button
                        type="button"
                        onClick={() => setIsNotificationPanelOpen((value) => !value)}
                        aria-label="Notifications (mobile)"
                        className="flex h-8 w-8 items-center justify-center rounded-full border border-transparent bg-transparent transition-colors hover:border-secondary hover:bg-hover"
                    >
                        <NotificationIcon active={false} padding={0} />
                    </button>
                </div>

                <div className="lg:hidden" ref={userMenuRef}>
                    <button
                        type="button"
                        onClick={handleUserMenuClick}
                        className="rounded-full"
                        aria-label="User menu (mobile)"
                    >
                        <Avatar color="#52525B" label={user?.email?.charAt(0).toUpperCase() || 'F'} />
                    </button>
                </div>

                <button
                    type="button"
                    onClick={() => setIsMembersModalOpen(true)}
                    aria-label="View workspace members"
                    className="hidden items-center rounded-full px-2 py-1 transition-colors hover:bg-hover lg:flex"
                >
                    <div className="flex -space-x-2">
                        {avatarMembers.map((avatar, index) => (
                            <Avatar
                                key={index}
                                label={avatar.label}
                                src={avatar.src}
                                color={avatar.bgColor}
                                size={7}
                            />
                        ))}
                    </div>
                </button>

                {currentWorkspace && (
                    <Button variant="ghost" size="sm" onClick={() => setIsShareModalOpen(true)}>
                        Share
                    </Button>
                )}

                <UpgradeButton text="Upgrade" fullWidth={false} />
            </div>

            {currentWorkspace && (
                <>
                    <ShareModal
                        isOpen={isShareModalOpen}
                        onClose={() => setIsShareModalOpen(false)}
                        workspaceId={currentWorkspace.id}
                    />
                    <WorkspaceMembersModal
                        isOpen={isMembersModalOpen}
                        onClose={() => setIsMembersModalOpen(false)}
                        workspaceId={currentWorkspace.id}
                    />
                    <UserMenu
                        isOpen={isUserMenuOpen}
                        onClose={() => setIsUserMenuOpen(false)}
                        anchorRef={userMenuRef}
                        onLogout={handleLogout}
                        onSettings={() => navigate('/settings')}
                    />
                    <NotificationPanel
                        isOpen={isNotificationPanelOpen}
                        onClose={() => setIsNotificationPanelOpen(false)}
                        anchorRef={bellRef}
                    />
                </>
            )}
        </div>
    );
};

export default Navbar;
